import { useEffect, useState } from "react";
import api, { fmtSeconds, fmtMoney, billable } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from "recharts";

const RANGES = [
  { label: "Daily (1d)", value: 1 },
  { label: "Weekly (7d)", value: 7 },
  { label: "Monthly (30d)", value: 30 },
];

export default function ReportsPage({ scope = "admin" }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("all");
  const [range, setRange] = useState(7);
  const [report, setReport] = useState(null);

  useEffect(() => {
    const path = scope === "client" ? "/client/users" : "/admin/users";
    api.get(path).then((r) => {
      const list = scope === "client" ? r.data : r.data.filter((u) => u.role === "employee");
      setUsers(list);
    });
  }, [scope]);

  useEffect(() => {
    const params = { range_days: range };
    if (userId !== "all") params.user_id = userId;
    api.get("/reports/summary", { params }).then((r) => setReport(r.data));
  }, [userId, range]);

  const exportCSV = () => {
    const rows = report?.sessions || [];
    const userMap = {}; users.forEach((u) => { userMap[u.id] = u; });
    const lines = [
      ["Date", "Employee", "Email", "Productive (s)", "Idle (s)", "Total (s)", "Productive (h)", "Rate", "Currency", "Billable"].join(","),
      ...rows.map((s) => {
        const u = userMap[s.user_id] || {};
        const hrs = s.productive_seconds / 3600;
        const bill = (u.hourly_rate || 0) * hrs;
        return [
          s.date,
          (u.name || "").replace(/,/g, ""),
          u.email || "",
          s.productive_seconds,
          s.idle_seconds,
          s.total_seconds,
          hrs.toFixed(2),
          (u.hourly_rate || 0).toFixed(2),
          u.currency || "",
          bill.toFixed(2),
        ].join(",");
      }),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `report-${range}d-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = (report?.sessions || []).map((s) => {
      const u = users.find((x) => x.id === s.user_id) || {};
      return `<tr><td>${s.date}</td><td>${u.name || ""}</td><td>${u.email || ""}</td><td style="text-align:right">${fmtSeconds(s.productive_seconds)}</td><td style="text-align:right">${fmtSeconds(s.idle_seconds)}</td><td style="text-align:right">${fmtSeconds(s.total_seconds)}</td></tr>`;
    }).join("");
    w.document.write(`<!doctype html><html><head><title>Attendance Report</title>
      <style>
        body{font-family: 'IBM Plex Mono', monospace; padding:24px; color:#0a0a0a}
        h1{font-family:'Space Grotesk',sans-serif;letter-spacing:-1px}
        table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px}
        th,td{border:1px solid #ddd;padding:8px}
        th{background:#f5f5f5;text-align:left;text-transform:uppercase;font-size:10px;letter-spacing:2px}
        .summary{display:flex;gap:24px;margin-top:8px;padding:12px;border:1px solid #ddd}
        .summary div{flex:1}
        .lbl{font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#777}
        .val{font-size:24px;font-weight:bold;color:#1f4d2e}
      </style></head><body>
      <h1>Attendance & Time Report</h1>
      <div>Range: last ${range} days · ${new Date().toLocaleDateString()}</div>
      <div class="summary">
        <div><div class="lbl">Total</div><div class="val">${((report?.totals?.total||0)/3600).toFixed(1)}h</div></div>
        <div><div class="lbl">Productive</div><div class="val">${((report?.totals?.productive||0)/3600).toFixed(1)}h</div></div>
        <div><div class="lbl">Idle</div><div class="val">${((report?.totals?.idle||0)/3600).toFixed(1)}h</div></div>
        <div><div class="lbl">Sessions</div><div class="val">${report?.totals?.sessions_count||0}</div></div>
      </div>
      <table>
        <thead><tr><th>Date</th><th>Employee</th><th>Email</th><th style="text-align:right">Productive</th><th style="text-align:right">Idle</th><th style="text-align:right">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script>
    </body></html>`);
    w.document.close();
  };

  const chartData = (report?.by_date || []).map((d) => ({
    date: d.date.slice(5),
    productive: +(d.productive / 3600).toFixed(2),
    idle: +(d.idle / 3600).toFixed(2),
  }));

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ Reports ]</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Attendance Reports</h1>
          </div>
          <div className="flex gap-2">
            <Button data-testid="export-csv-button" onClick={exportCSV} variant="outline" className="rounded-md border-stone-300">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button data-testid="export-pdf-button" onClick={exportPDF} className="rounded-md bg-[#1f4d2e] hover:bg-[#0f3a1f] text-stone-100 transition-elegant">
              <Download className="h-4 w-4 mr-2" /> Export PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 border border-stone-200 p-4 bg-stone-50">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold mb-1">Range</div>
            <Select value={String(range)} onValueChange={(v) => setRange(Number(v))}>
              <SelectTrigger className="rounded-md bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">
                {RANGES.map((r) => <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold mb-1">Employee</div>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="rounded-md bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="all">All employees</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        {(() => {
          // Compute billable totals (group by currency since employees may differ)
          const byCurrency = {};
          (report?.sessions || []).forEach((s) => {
            const u = users.find((x) => x.id === s.user_id);
            if (!u || !u.hourly_rate) return;
            const cur = u.currency || "PHP";
            byCurrency[cur] = (byCurrency[cur] || 0) + billable(s.productive_seconds, u.hourly_rate);
          });
          const currencyList = Object.entries(byCurrency);
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-3">
                {[
                  { l: "Total", v: ((report?.totals?.total || 0) / 3600).toFixed(1), s: "h" },
                  { l: "Productive", v: ((report?.totals?.productive || 0) / 3600).toFixed(1), s: "h", color: "text-[#1f4d2e]" },
                  { l: "Idle / Paused", v: ((report?.totals?.idle || 0) / 3600).toFixed(1), s: "h", color: "text-stone-500" },
                  { l: "Sessions", v: report?.totals?.sessions_count || 0 },
                ].map((k) => (
                  <div key={k.l} className="bg-white p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-2">{k.l}</div>
                    <div className={`font-mono-num text-3xl font-bold ${k.color || ""}`}>
                      {k.v}{k.s && <span className="text-base text-stone-400 ml-1">{k.s}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {currencyList.length > 0 && (
                <div className="mb-6 p-5 rounded-md border border-[#d4a82a]/40 bg-[#d4a82a]/10 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <span className="gold-rule" />
                    <span className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#7a5e0f]">Billable amount</span>
                  </div>
                  <div className="flex gap-8 flex-wrap">
                    {currencyList.map(([cur, amt]) => (
                      <div key={cur}>
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500">{cur}</div>
                        <div className="font-mono-num text-2xl font-bold text-[#1f4d2e]">
                          {fmtMoney(amt, cur)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Quality breakdown */}
              {(() => {
                const t = report?.totals || {};
                const productive = t.productive || 0;
                const lightS = t.light || 0;
                const engagedS = t.engaged || Math.max(0, productive - lightS);
                const idleS = t.idle || 0;
                const totalAll = productive + idleS;
                const lightR = productive > 0 ? Math.round((lightS / productive) * 100) : 0;
                const engagedR = productive > 0 ? Math.round((engagedS / productive) * 100) : 0;
                if (productive === 0 && idleS === 0) return null;
                return (
                  <div className="mb-6 border border-stone-200 bg-white rounded-md p-5" data-testid="reports-activity-quality">
                    <div className="flex items-baseline justify-between mb-1 flex-wrap gap-2">
                      <h3 className="font-display text-xl font-medium text-[#1f4d2e]">Activity Quality</h3>
                      <div className="text-xs tracking-[0.22em] uppercase font-bold text-stone-500">
                        Engaged {engagedR}% · Light {lightR}%
                      </div>
                    </div>
                    <p className="font-display-italic text-xs text-stone-500 mb-4">
                      Engaged = clicks + keystrokes detected. Light = only mouse movement (possible fake-activity pattern).
                    </p>
                    <div className="h-3 w-full bg-stone-100 rounded-full overflow-hidden flex">
                      {totalAll > 0 && (
                        <>
                          <div className="h-full bg-[#1f4d2e]" style={{ width: `${(engagedS / totalAll) * 100}%` }} />
                          <div className="h-full bg-[#d4a82a]" style={{ width: `${(lightS / totalAll) * 100}%` }} />
                          <div className="h-full bg-stone-300" style={{ width: `${(idleS / totalAll) * 100}%` }} />
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-3 mt-4 gap-px bg-stone-200 border border-stone-200">
                      <div className="bg-white p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="h-2 w-2 rounded-sm bg-[#1f4d2e]" />
                          <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-600">Engaged</span>
                        </div>
                        <div className="font-mono-num text-xl font-semibold text-[#1f4d2e]">{fmtSeconds(engagedS)}</div>
                      </div>
                      <div className="bg-white p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="h-2 w-2 rounded-sm bg-[#d4a82a]" />
                          <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-600">Light</span>
                        </div>
                        <div className={`font-mono-num text-xl font-semibold ${lightR > 30 ? "text-[#a8421f]" : "text-[#7a5e0f]"}`}>{fmtSeconds(lightS)}</div>
                      </div>
                      <div className="bg-white p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="h-2 w-2 rounded-sm bg-stone-400" />
                          <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-600">Idle</span>
                        </div>
                        <div className="font-mono-num text-xl font-semibold text-stone-500">{fmtSeconds(idleS)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mt-3">
                      {[
                        { l: "Keystrokes", v: t.keystrokes || 0 },
                        { l: "Clicks", v: t.mouse_clicks || 0 },
                        { l: "Mouse moves", v: t.mouse_moves || 0 },
                        { l: "Scrolls", v: t.scrolls || 0 },
                      ].map((x) => (
                        <div key={x.l} className="bg-white p-3">
                          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">{x.l}</div>
                          <div className="font-mono-num text-lg font-semibold mt-0.5">{x.v.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          );
        })()}

        {/* Chart */}
        <div className="border border-stone-200 bg-white p-6 mb-6">
          <div className="text-xs tracking-[0.25em] uppercase font-bold mb-4">Hours by Day</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid stroke="#e5e5e5" vertical={false} />
                <XAxis dataKey="date" stroke="#737373" fontSize={11} fontFamily="IBM Plex Mono" />
                <YAxis stroke="#737373" fontSize={11} fontFamily="IBM Plex Mono" />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #e5e5e5", fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11 }} />
                <Bar dataKey="productive" stackId="a" fill="#1f4d2e" name="Productive" />
                <Bar dataKey="idle" stackId="a" fill="#d4d4d4" name="Idle" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table */}
        <div className="border border-stone-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-stone-50 border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Date</th>
                <th className="text-left px-4 py-3 font-bold">Employee</th>
                <th className="text-right px-4 py-3 font-bold">Productive</th>
                <th className="text-right px-4 py-3 font-bold">Idle</th>
                <th className="text-right px-4 py-3 font-bold">Total</th>
                <th className="text-right px-4 py-3 font-bold">Billable</th>
              </tr>
            </thead>
            <tbody>
              {(report?.sessions || []).map((s) => {
                const u = users.find((x) => x.id === s.user_id);
                const billableAmt = u?.hourly_rate ? billable(s.productive_seconds, u.hourly_rate) : 0;
                return (
                  <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono-num">{s.date}</td>
                    <td className="px-4 py-3">{u?.name || s.user_id.slice(0,8)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-[#1f4d2e] font-bold">{fmtSeconds(s.productive_seconds)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-stone-500">{fmtSeconds(s.idle_seconds)}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-bold">{fmtSeconds(s.total_seconds)}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-semibold">
                      {u?.hourly_rate ? (
                        <span className="text-[#1f4d2e]">{fmtMoney(billableAmt, u.currency)}</span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(report?.sessions || []).length === 0 && (
                <tr><td colSpan="6" className="text-center py-12 text-sm text-stone-500">No data for this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
